/**
 * PlanGate — wraps a feature behind a plan-tier check.
 *
 * Usage:
 *   <PlanGate minimum="growth">
 *     <ReportExportButton />
 *   </PlanGate>
 *
 * When the org's tier is below `minimum`, renders an upgrade prompt instead
 * of the children.  Pass `inline` for a compact inline badge variant.
 */
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, Zap } from 'lucide-react'
import { usePlan } from '../../hooks/usePlan'
import { PlanTier, PLAN_META } from '../../services/billing'

interface PlanGateProps {
  minimum: PlanTier
  children: React.ReactNode
  /** Show a small inline badge instead of a full block */
  inline?: boolean
  /** Override the feature name shown in the upgrade prompt */
  featureName?: string
}

export function PlanGate({ minimum, children, inline = false, featureName }: PlanGateProps) {
  const { can, loading } = usePlan()
  const navigate = useNavigate()

  // While loading, render children optimistically (avoids flash of upgrade prompts).
  if (loading) return <>{children}</>

  if (can(minimum)) return <>{children}</>

  const plan = PLAN_META[minimum] ?? PLAN_META.growth

  if (inline) {
    return (
      <button
        onClick={() => navigate(`/billing?plan=${minimum}`)}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold
                   bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors cursor-pointer border-0"
        title={`Requires ${plan.label} plan`}
      >
        <Lock size={10} />
        {plan.label}
      </button>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center gap-3 p-8 rounded-xl
                    border-2 border-dashed border-violet-200 bg-violet-50/50 text-center">
      <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
        <Lock size={18} className="text-violet-600" />
      </div>
      <div>
        <p className="font-semibold text-foreground">
          {featureName ? `${featureName} requires` : 'Requires'} the{' '}
          <span className="text-violet-600">{plan.label} plan</span>
        </p>
        <p className="text-sm text-muted-foreground mt-0.5">{plan.price} · Upgrade anytime, cancel anytime.</p>
      </div>
      <button
        onClick={() => navigate(`/billing?plan=${minimum}`)}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white
                   text-sm font-semibold hover:bg-violet-700 transition-colors"
      >
        <Zap size={14} />
        Upgrade to {plan.label}
      </button>
    </div>
  )
}
